import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

export type PhaseData = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

export const phases: PhaseData[] = [
  {
    id: 'library',
    eyebrow: '',
    title: 'Pick a book. Jump in.',
    description: 'Start exactly where you left off — Vidya remembers your place.',
    bullets: [
      'Your progress carries forward automatically',
      "Jump to the chapter you're studying today",
      'No setup, no re-learning',
    ],
    icon: Squares2X2Icon,
  },
  {
    id: 'highlight',
    eyebrow: '',
    title: 'Mark what matters.',
    description: 'Capture key ideas as you read — Vidya keeps the context for later.',
    bullets: [
      'Save ideas in the moment',
      'Context stays attached (chapter, concept)',
      'Nothing extra to organize',
    ],
    icon: SparklesIcon,
  },
  {
    id: 'ask',
    eyebrow: '',
    title: 'Ask & understand.',
    description: "Vidya answers using what you've already read — not generic explanations.",
    bullets: [
      'Answers grounded in your book',
      'Connections to earlier ideas',
      'Explanations that actually click',
    ],
    icon: ChatBubbleLeftRightIcon,
  },
  {
    id: 'remember',
    eyebrow: '',
    title: 'Come back — it still remembers.',
    description: 'Your learning stays connected, so revision feels calm — not overwhelming.',
    bullets: [
      "Everything you've learned, in one place",
      'Fast recall before exams',
      'Pick up exactly where you left off',
    ],
    icon: BookOpenIcon,
  },
];

export const sectionHeader = {
  eyebrow: 'Product Demo',
  headline: 'Learning should stack — not reset.',
  subhead: "Vidya answers in the context of what you've already learned — so every session builds forward.",
};

export const askPhaseContent = {
  question: 'How do functional groups change what a molecule does?',
  answerPills: ['In your book • p.77', 'In your context'],
  answer:
    'Functional groups are the "business end" of a molecule — they\'re the parts that react and shape properties like polarity and solubility.',
  connections: [
    'Isomers differ because functional groups shift position',
    'Amino acids are built from amino + carboxyl groups',
    'Structure → function is the pattern you\'re learning',
  ],
  visualRef: {
    title: 'Functional Groups Chart',
    page: 77,
  },
  context: {
    lastRead: 'Carbon backbones',
    buildsOn: ['polarity', 'hydrophilic vs hydrophobic'],
  },
};

export const frostGradient = 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)';

export const bookData = [
  {
    name: 'Biology 2e',
    subject: 'Life Sciences',
    gradient: 'from-cyan-500 to-blue-500',
    emoji: '🧬',
    chapter: 'Ch 2',
    progress: 37,
  },
  {
    name: 'Chemistry 2e',
    subject: 'Physical Sciences',
    gradient: 'from-sky-500 to-cyan-500',
    emoji: '⚗️',
    chapter: 'Ch 4',
    progress: 62,
  },
  {
    name: 'College Physics',
    subject: 'Physical Sciences',
    gradient: 'from-blue-500 to-sky-500',
    emoji: '⚛️',
    chapter: 'Ch 1',
    progress: 15,
  },
  {
    name: 'Psychology 2e',
    subject: 'Behavioral Sciences',
    gradient: 'from-teal-500 to-cyan-500',
    emoji: '🧠',
    chapter: 'Ch 3',
    progress: 88,
  },
];
